import { useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';
import type { Condition } from '@locus-taxy/encore-grid';

// API wrapper from backend
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// GET /api/v1/view/clients/{clientId}/modules
export type ModulesApiResponse = ApiResponse<ModulesData>;

// When no config is set, data will be {} with a message
export type ModulesData = ModulesConfig | Record<string, never>;

export interface ModulesConfig {
  id: string;
  modules: ModuleItem[];
  bottomModules?: ModuleItem[];
  /**
   * `appId → { name, remoteApp }` for every app in the client's registry
   * (DP-5201). Absent when the client registers no apps.
   *
   * This is the whole app registry, not just the `APP` leaves of the navigation
   * tree — custom apps also mount from workflow actions and from entity tabs,
   * and those need not appear in navigation at all. Read it through
   * `useAppRegistry`, which applies the value checks.
   */
  apps?: Record<string, AppRegistryEntry>;
}

/** One entry of `ModulesConfig.apps`. */
export interface AppRegistryEntry {
  /** The app's registered display name — what a custom-app page is titled. */
  name?: string;
  /** The id of the app whose bundle serves this one. Absent → its own id. */
  remoteApp?: string;
}

export type ModuleItem = EntityLeaf | AppLeaf | FrontendRouteLeaf | NavNode;

export interface BaseLeaf {
  id?: string;
  label?: string;
  order?: number;
  icon?: string;
}

export interface EntityLeaf extends BaseLeaf {
  type: 'ENTITY';
  entityName: string;
  /**
   * Usecase slug (DP-3570). When present, this leaf points at the entity's
   * usecase page (`/entities/{entityName}/uc/{usecase}`) instead of the default
   * page. Nav visibility for a usecase leaf is gated by the backend modules
   * filter against the `entityUsecase` registry record; the dashboard just
   * routes wherever the leaf points. Absent → the default entity page.
   */
  usecase?: string;
  /**
   * Render a record count beside this leaf's label (DP-6112). Defaults to
   * false; a leaf without it fires no aggregate request at all.
   */
  showCount?: boolean;
  /**
   * The condition the badge counts over. Absent → every record of the entity.
   *
   * Deliberately the badge's *only* variable input: the count is meant to read
   * "records in this view", so it must not move as the user filters the entity
   * page. See `useNavBadgeCounts`.
   */
  countCondition?: Condition;
  /**
   * How often, in seconds, the badge re-counts while the dashboard is open
   * (DP-6112). Absent → counted once, when the nav loads. The cadence is the
   * module's own: a nav that polls is a decision about the nav, not something
   * inherited from whatever the entity's list config happens to say.
   */
  countRefetchInterval?: number;
}

export interface AppLeaf extends BaseLeaf {
  type: 'APP';
  appName: string;
  appOverrides?: {
    name?: string;
    link?: string; // URI
    description?: string;
  };
}

export interface FrontendRouteLeaf extends BaseLeaf {
  type: 'FRONTEND_ROUTE';
  route: string;
}

export interface NavNode {
  id: string;
  key: string;
  label: string;
  icon?: string;
  order?: number;
  items: ModuleItem[];
}

export function useModules(clientId: string) {
  const { showApiError } = useToast();
  return useApiQuery<ModulesApiResponse>({
    key: ['modules', clientId],
    endpointKey: 'modules.list',
    params: { clientId },
    config: {
      staleTime: 5 * 60 * 1000,
      enabled: Boolean(clientId),
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
  });
}
